/**
 * Promoting a protocol in three steps. Check: the protocol must be live and compiled, with cleared
 * scenarios and templates, or the flow stops and says what to fix. Scenarios: each scenario's reply is a
 * Draft or Auto-send and each of its writes Ask first or Run without asking; everything starts safe, and a
 * setting the evidence does not support is locked with the reason. Review and promote: one summary, a
 * blocker for a write that runs while its reply drafts, a warning for a reply that waits on approval, and
 * Promote applies all of it at once.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Check, Loader2, Lock, ShieldCheck, X } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { usePromoteProtocol, usePromotionPlan, usePromotionPreview } from '@/hooks/use-promotion';
import { cn, getApiErrorMessage } from '@/lib/utils';
import { counted } from '@/modules/back-testing/format';
import { ReadinessBadge } from '@/modules/back-testing/readiness/readiness-badge';
import { ApprovalThreshold } from './approval-threshold';
import { AdditionList, ImpactList } from './impact-list';
import {
	blockersOf,
	initialSettings,
	draftIdsToAdd,
	isPromotable,
	isUnchangedPromotion,
	summaryOf,
	warningsOf,
	withAllReplies,
	withReply,
	withWrite
} from './promote-flow';
import type { PromotionCheck, PromotionPreview, PromotionType, ScenarioSetting } from './types';

const STEPS = ['Check', 'Scenarios', 'Review and promote'] as const;

interface ChoiceOption<T extends string> {
	value: T;
	label: string;
}

interface ChoiceProps<T extends string> {
	value: T;
	options: [ChoiceOption<T>, ChoiceOption<T>];
	onChange: (value: T) => void;
	/** Locks the second (riskier) option, with the reason shown beside it. */
	locked: boolean;
	label: string;
}

/** A two-way switch where the second option is the one that needs evidence. */
const Choice = <T extends string>({ value, options, onChange, locked, label }: ChoiceProps<T>) => (
	<div role='radiogroup' aria-label={label} className='inline-flex shrink-0 rounded-md border border-gray-400 p-0.5'>
		{options.map((option, position) => {
			const disabled = position === 1 && locked;
			return (
				<button
					key={option.value}
					type='button'
					role='radio'
					aria-checked={value === option.value}
					disabled={disabled}
					onClick={() => onChange(option.value)}
					className={cn(
						'inline-flex items-center gap-1 rounded px-2 py-0.5 text-[11px] transition-colors',
						value === option.value ? 'bg-ink text-white' : 'text-gray-900 hover:bg-gray-100',
						disabled && 'cursor-not-allowed opacity-50 hover:bg-transparent'
					)}
				>
					{disabled && <Lock className='size-3' aria-hidden />}
					{option.label}
				</button>
			);
		})}
	</div>
);

const CheckStep = ({ check }: { check: PromotionCheck }) => (
	<ul className='flex flex-col gap-2' data-testid='promotion-check'>
		{check.items.map((item) => (
			<li key={item.key} className='flex items-start gap-2 text-sm'>
				{item.pass ? (
					<Check className='mt-0.5 size-4 shrink-0 text-green-700' aria-label='passes' />
				) : (
					<X className='mt-0.5 size-4 shrink-0 text-red-700' aria-label='fails' />
				)}
				<span>
					{item.label}
					{!item.pass && <span className='block text-xs text-gray-800'>{item.fix}</span>}
				</span>
			</li>
		))}
	</ul>
);

interface ScenarioCardProps {
	type: PromotionType;
	types: PromotionType[];
	settings: Record<string, ScenarioSetting>;
	onChange: (settings: Record<string, ScenarioSetting>) => void;
	focused: boolean;
}

/** The template's first line; the whole text a click away, so one long template does not bury the next scenario. */
const TemplatePreview = ({ text }: { text: string }) => {
	const [expanded, setExpanded] = useState(false);
	return (
		<div className='flex flex-col items-start gap-0.5'>
			<p className={cn('text-[12px] whitespace-pre-wrap text-gray-900', !expanded && 'line-clamp-1')}>{text}</p>
			<button
				type='button'
				className='text-[11px] font-medium text-indigo-700 hover:text-indigo-900'
				onClick={() => setExpanded((open) => !open)}
			>
				{expanded ? 'Hide template' : 'Show template'}
			</button>
		</div>
	);
};

const ScenarioCard = ({ type, types, settings, onChange, focused }: ScenarioCardProps) => {
	const setting = settings[type.typeId]!;
	const template = type.reply.templates[0];
	const draft = type.reply.draft ?? null;
	const nameOf = (typeId: string): string => types.find((other) => other.typeId === typeId)?.description ?? typeId;
	return (
		<li
			className={cn('flex flex-col gap-2 rounded-lg border border-gray-300 p-3', focused && 'ring-2 ring-indigo-200')}
			data-testid={`scenario-${type.typeId}`}
			data-scenario-id={type.typeId}
		>
			<div className='flex items-start justify-between gap-2'>
				<p className='min-w-0 flex-1 text-[13px] font-medium'>{type.description}</p>
				<ReadinessBadge readiness={type.readiness} className='text-[10px]' />
			</div>
			<div className='flex flex-col gap-1.5 rounded-md bg-gray-50 p-2'>
				<div className='flex items-center justify-between gap-2'>
					<span className='text-[11px] font-medium text-gray-900'>Reply</span>
					<Choice
						label={`Reply for ${type.description}`}
						value={setting.reply}
						options={[
							{ value: 'draft', label: 'Draft' },
							{ value: 'auto', label: 'Auto-send' }
						]}
						locked={type.reply.lockedReason !== null}
						onChange={(reply) => onChange(withReply(settings, type.typeId, reply))}
					/>
				</div>
				{template && <TemplatePreview text={template.text} />}
				{draft && (
					<div className='flex flex-col gap-0.5' data-testid={`draft-${type.typeId}`}>
						<p className='text-[11px] font-medium text-amber-700'>
							{draft.addedOnPromote
								? 'New template, not live until you promote'
								: 'New template, waits until back testing clears this scenario'}
						</p>
						<TemplatePreview text={draft.text} />
					</div>
				)}
				{!template && !draft && <p className='text-[11px] text-gray-800'>No template yet.</p>}
				{type.reply.lockedReason && <p className='text-[11px] text-gray-800'>{type.reply.lockedReason}</p>}
			</div>
			{type.writes.map((write) => {
				const mode = setting.writes[write.tool] ?? 'ask';
				return (
					<div
						key={write.tool}
						className='flex flex-col gap-1 rounded-md border border-dashed border-gray-400 p-2'
						data-testid={`write-${type.typeId}-${write.tool}`}
					>
						<div className='flex items-center justify-between gap-2'>
							<span className='flex min-w-0 items-center gap-1.5'>
								<code className='truncate text-[12px]'>{write.tool}</code>
								<Badge
									variant={write.streak >= write.required ? 'success' : 'secondary'}
									className='text-[10px]'
									title='Supervisor approvals in a row'
								>
									{write.streak}/{write.required} approvals
								</Badge>
							</span>
							<Choice
								label={`${write.tool} for ${type.description}`}
								value={mode}
								options={[
									{ value: 'ask', label: 'Ask first' },
									{ value: 'auto', label: 'Run without asking' }
								]}
								locked={write.lockedReason !== null}
								onChange={(next) => onChange(withWrite(settings, type.typeId, write.tool, next))}
							/>
						</div>
						{write.lockedReason && <p className='text-[11px] text-gray-800'>{write.lockedReason}</p>}
						{write.rules.length > 0 && (
							<div className='flex flex-wrap gap-1'>
								{write.rules.map((rule) => (
									<Badge key={rule} variant='outline' className='text-[10px]'>
										Only when {rule}
									</Badge>
								))}
							</div>
						)}
						{write.sharedWith.length > 0 && (
							<p className='text-[11px] text-gray-800'>
								Also reached by {write.sharedWith.map(nameOf).join(', ')}; this setting applies to this scenario only.
							</p>
						)}
					</div>
				);
			})}
		</li>
	);
};

/** A scenario Promote cannot change, on one line: it stays a draft that asks first. */
const ScenarioRow = ({ type, focused }: { type: PromotionType; focused: boolean }) => (
	<li
		className={cn('flex items-center justify-between gap-2 px-3 py-1.5', focused && 'bg-gray-50 ring-2 ring-indigo-200')}
		data-testid={`scenario-row-${type.typeId}`}
		data-scenario-id={type.typeId}
		title={type.reply.lockedReason ?? undefined}
	>
		<span className='min-w-0 flex-1 truncate text-[12px]'>{type.description}</span>
		<ReadinessBadge readiness={type.readiness} className='shrink-0 text-[10px]' />
	</li>
);

interface ScenariosStepProps {
	types: PromotionType[];
	settings: Record<string, ScenarioSetting>;
	onChange: (settings: Record<string, ScenarioSetting>) => void;
	/** The scenario Promote was opened from: scrolled to and outlined. */
	focusTypeId: string | null;
}

/**
 * The scenarios Promote can change first, with their switches; the rest folded to one line each under All.
 * The split is taken once, so a card never moves while it is being set.
 */
const ScenariosStep = ({ types, settings, onChange, focusTypeId }: ScenariosStepProps) => {
	const [{ promotable, rest }] = useState(() => ({
		promotable: types.filter(isPromotable),
		rest: types.filter((type) => !isPromotable(type))
	}));
	const [filter, setFilter] = useState<'ready' | 'all'>(() =>
		promotable.length > 0 && !rest.some((type) => type.typeId === focusTypeId) ? 'ready' : 'all'
	);
	const listRef = useRef<HTMLDivElement>(null);
	useEffect(() => {
		if (!focusTypeId) return;
		const target = [...(listRef.current?.querySelectorAll<HTMLElement>('[data-scenario-id]') ?? [])].find(
			(element) => element.dataset['scenarioId'] === focusTypeId
		);
		target?.scrollIntoView?.({ block: 'center' });
	}, [focusTypeId]);

	const autoSending = promotable.filter((type) => settings[type.typeId]?.reply === 'auto').length;
	const canAutoSend = promotable.some((type) => type.reply.lockedReason === null);
	const filterButton = (value: 'ready' | 'all', label: string) => (
		<button
			type='button'
			aria-pressed={filter === value}
			onClick={() => setFilter(value)}
			className={cn(
				'rounded-md border px-2.5 py-0.5 text-[11px] transition-colors',
				filter === value ? 'border-indigo-300 bg-indigo-50 text-indigo-700' : 'border-gray-400 text-gray-900 hover:bg-gray-100'
			)}
		>
			{label}
		</button>
	);

	return (
		<div ref={listRef} className='flex max-h-[60vh] flex-col gap-3 overflow-y-auto'>
			<div className='flex flex-wrap items-center justify-between gap-2 rounded-md bg-gray-50 px-3 py-2' data-testid='promotion-scenarios-summary'>
				<p className='text-[12px]'>
					{promotable.length > 0 ? (
						<>
							<span className='font-medium'>
								{autoSending} of {counted(promotable.length, 'ready scenario')} set to auto-send
							</span>
							{rest.length > 0 && <span className='text-gray-800'> · {rest.length} not ready stay drafts</span>}
						</>
					) : (
						<span className='text-gray-800'>No scenario is ready yet, so every reply stays a draft.</span>
					)}
				</p>
				{canAutoSend && (
					<div className='flex gap-1.5'>
						<Button size='sm' variant='outline' className='h-7 text-[11px]' onClick={() => onChange(withAllReplies(promotable, settings, 'auto'))}>
							Auto-send all ready
						</Button>
						<Button size='sm' variant='outline' className='h-7 text-[11px]' onClick={() => onChange(withAllReplies(promotable, settings, 'draft'))}>
							All drafts
						</Button>
					</div>
				)}
			</div>
			<div className='flex items-start gap-2 rounded-md bg-blue-bg p-2 text-[12px]'>
				<ShieldCheck className='mt-0.5 size-4 shrink-0' aria-hidden />
				<ApprovalThreshold />
			</div>
			<div className='flex gap-1.5' role='group' aria-label='Which scenarios to show'>
				{filterButton('ready', `Ready ${promotable.length}`)}
				{filterButton('all', `All ${types.length}`)}
			</div>
			{promotable.length > 0 && (
				<section className='flex flex-col gap-1.5'>
					<h4 className='text-[11px] text-gray-800'>Ready</h4>
					<ul className='flex flex-col gap-2'>
						{promotable.map((type) => (
							<ScenarioCard
								key={type.typeId}
								type={type}
								types={types}
								settings={settings}
								onChange={onChange}
								focused={type.typeId === focusTypeId}
							/>
						))}
					</ul>
				</section>
			)}
			{filter === 'all' && rest.length > 0 && (
				<section className='flex flex-col gap-1.5'>
					<h4 className='text-[11px] text-gray-800'>Not ready · stay drafts</h4>
					<ul className='divide-y divide-gray-300 rounded-lg border border-gray-300'>
						{rest.map((type) => (
							<ScenarioRow key={type.typeId} type={type} focused={type.typeId === focusTypeId} />
						))}
					</ul>
				</section>
			)}
		</div>
	);
};

interface ReviewStepProps {
	types: PromotionType[];
	settings: Record<string, ScenarioSetting>;
	intents: string[];
	preview: PromotionPreview | null | undefined;
	previewLoading: boolean;
	previewError: boolean;
}

const ReviewStep = ({ types, settings, intents, preview, previewLoading, previewError }: ReviewStepProps) => {
	const summary = summaryOf(types, settings);
	const blockers = blockersOf(types, settings);
	const warnings = warningsOf(types, settings);
	const nameOf = (typeId: string): string => types.find((type) => type.typeId === typeId)?.description ?? typeId;
	return (
		<div className='flex flex-col gap-3 text-sm' data-testid='promotion-review'>
			<section>
				<h4 className='text-xs font-semibold'>Sends by itself</h4>
				{summary.autoReplies.length ? (
					<ul className='list-disc pl-5 text-[13px]'>
						{summary.autoReplies.map((type) => (
							<li key={type.typeId}>{type.description}</li>
						))}
					</ul>
				) : (
					<p className='text-[13px] text-gray-800'>Nothing; every reply stays a draft.</p>
				)}
				{summary.draftReplies.length > 0 && (
					<p className='text-[12px] text-gray-800'>{counted(summary.draftReplies.length, 'scenario')} stay drafts.</p>
				)}
			</section>
			<section>
				<h4 className='text-xs font-semibold'>Runs without asking</h4>
				<p className='text-[13px]'>
					{summary.autoWrites.length ? summary.autoWrites.join(', ') : 'Nothing; every write asks a supervisor first.'}
				</p>
			</section>
			<section>
				<h4 className='text-xs font-semibold'>Intents this protocol covers</h4>
				<p className='text-[13px]'>{intents.length ? intents.join(', ') : 'None yet'}</p>
			</section>
			{(previewLoading || (preview?.additions?.length ?? 0) > 0) && (
				<section className='flex flex-col gap-1' data-testid='promotion-additions'>
					<h4 className='text-xs font-semibold'>Adds new templates</h4>
					<p className='text-[12px] text-gray-800'>
						Generated, never live. Promote adds them, with their steps and rules, to these blueprints.
					</p>
					<AdditionList additions={preview?.additions ?? []} loading={previewLoading} />
				</section>
			)}
			<section className='flex flex-col gap-1' data-testid='promotion-impact'>
				<h4 className='text-xs font-semibold'>Also changes on the promoted blueprints</h4>
				<ImpactList preview={preview} loading={previewLoading} error={previewError} />
			</section>
			{blockers.length > 0 && (
				<ul className='flex flex-col gap-1.5' data-testid='promotion-blockers'>
					{blockers.map((blocker) => (
						<li key={blocker.typeId} className='flex items-start gap-1.5 text-[12px] text-destructive'>
							<X className='mt-0.5 size-3.5 shrink-0' aria-hidden />
							<span>
								<span className='font-medium'>{nameOf(blocker.typeId)}:</span> {blocker.message}
							</span>
						</li>
					))}
				</ul>
			)}
			{warnings.length > 0 && (
				<ul className='flex flex-col gap-1.5' data-testid='promotion-warnings'>
					{warnings.map((warning) => (
						<li key={`${warning.typeId}:${warning.kind}`} className='flex items-start gap-1.5 text-[12px] text-amber-700'>
							<AlertTriangle className='mt-0.5 size-3.5 shrink-0' aria-hidden />
							<span>
								<span className='font-medium'>{nameOf(warning.typeId)}:</span> {warning.message}
							</span>
						</li>
					))}
				</ul>
			)}
		</div>
	);
};

interface PromoteDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	sopId: string;
	/** The scenario Promote was opened from; the Scenarios step scrolls to it. */
	focusTypeId?: string | null;
}

export const PromoteDialog = ({ open, onOpenChange, sopId, focusTypeId = null }: PromoteDialogProps) => {
	const { data: plan, isLoading, isError } = usePromotionPlan(open ? sopId : '');
	const promote = usePromoteProtocol();
	const preview = usePromotionPreview();
	const [step, setStep] = useState(0);
	const [settings, setSettings] = useState<Record<string, ScenarioSetting> | null>(null);
	const startingSettings = useMemo(() => (plan ? initialSettings(plan) : null), [plan]);

	// Start over each time the dialog opens, from what is in force.
	useEffect(() => {
		if (!open) {
			setStep(0);
			setSettings(null);
		}
	}, [open]);
	useEffect(() => {
		if (open && startingSettings && settings === null) setSettings(startingSettings);
	}, [open, startingSettings, settings]);
	// The review shows what the chosen settings change on every blueprint, so it is fetched on arrival there.
	const reviewStep = step === STEPS.length - 1;
	useEffect(() => {
		if (reviewStep && settings) preview.mutate({ sopId, scenarios: settings });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [reviewStep, settings, sopId]);

	const submit = async () => {
		if (!settings) return;
		try {
			const record = await promote.mutateAsync({
				sopId,
				scenarios: settings,
				drafts: plan ? draftIdsToAdd(plan) : []
			});
			toast.success(
				record.autoWrites.length
					? `Promoted. ${counted(record.autoWrites.length, 'write')} run without asking.`
					: 'Promoted. Every write still asks first.'
			);
			onOpenChange(false);
		} catch (error) {
			toast.error(getApiErrorMessage(error, 'Could not promote this protocol'));
		}
	};

	const ready = plan && settings;
	// Nothing to promote: the protocol is promoted with exactly these settings. The backend refuses it too.
	const unchanged = Boolean(plan && settings && isUnchangedPromotion(plan, settings));
	// The backend refuses these too.
	const blocked = Boolean(plan && settings && blockersOf(plan.types, settings).length);
	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className='sm:max-w-2xl'>
				<DialogHeader>
					<DialogTitle>
						Promote <span className='capitalize'>{plan?.name ?? 'protocol'}</span>
					</DialogTitle>
					<DialogDescription>
						Step {step + 1} of {STEPS.length}: {STEPS[step]}
					</DialogDescription>
				</DialogHeader>

				{isError ? (
					<p className='text-xs text-destructive'>Could not load this protocol.</p>
				) : isLoading || !ready ? (
					<div className='flex flex-col gap-2'>
						<Skeleton className='h-5 w-48' />
						<Skeleton className='h-24 w-full' />
					</div>
				) : step === 0 ? (
					<CheckStep check={plan.check} />
				) : step === 1 ? (
					<ScenariosStep types={plan.types} settings={settings} onChange={setSettings} focusTypeId={focusTypeId} />
				) : (
					<ReviewStep
						types={plan.types}
						settings={settings}
						intents={plan.intents}
						preview={preview.data}
						previewLoading={preview.isPending}
						previewError={preview.isError}
					/>
				)}

				<DialogFooter>
					{step > 0 && (
						<Button variant='outline' onClick={() => setStep(step - 1)}>
							Back
						</Button>
					)}
					{step < STEPS.length - 1 ? (
						<Button onClick={() => setStep(step + 1)} disabled={!ready || (step === 0 && !plan.check.ok)}>
							Next
						</Button>
					) : (
						<>
							{unchanged && (
								<p className='self-center text-[11px] text-gray-800' data-testid='promotion-unchanged'>
									Already promoted with these settings.
								</p>
							)}
							<Button onClick={submit} disabled={!ready || promote.isPending || unchanged || blocked}>
								{promote.isPending && <Loader2 className='size-3.5 animate-spin' />}
								{plan?.promotion?.status === 'active' ? 'Update promotion' : 'Promote'}
							</Button>
						</>
					)}
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};
